import { useMemo } from 'react';
import { useQuery } from './use-query';
import {
  GET_STOCK_LOCATIONS,
  GET_MY_STOCK_LOCATION_ASSIGNMENTS,
} from '@/lib/graphql/operations/inventory';

interface StockLocation {
  id: string;
  name: string;
  description?: string;
  customFields?: Record<string, unknown>;
}

interface StockLocationsData {
  stockLocations: {
    items: StockLocation[];
    totalItems: number;
  };
}

interface AssignmentsData {
  myStockLocationAssignments: string[];
}

/**
 * Shared hook that returns stock locations filtered by the current user's
 * staff-stock-location assignments.
 *
 * - If the user has NO assignments → returns ALL locations (default, unrestricted).
 * - If the user has assignments → returns ONLY the assigned locations.
 *
 * This replaces direct `GET_STOCK_LOCATIONS` calls across all components
 * that need location-aware filtering.
 */
export function useAccessibleLocations() {
  const { data: locationsData, loading: loadingLocations, refetch: refetchLocations } =
    useQuery<StockLocationsData>(GET_STOCK_LOCATIONS, {
      options: { take: 100 },
    });

  const { data: assignmentsData, loading: loadingAssignments } =
    useQuery<AssignmentsData>(GET_MY_STOCK_LOCATION_ASSIGNMENTS, {});

  // Stabilize references — only recompute when the underlying data object changes
  const allLocations = useMemo(
    () => locationsData?.stockLocations?.items || [],
    [locationsData],
  );

  const assignedIds = useMemo(
    () => assignmentsData?.myStockLocationAssignments || [],
    [assignmentsData],
  );

  const assignedIdSet = useMemo(() => new Set(assignedIds), [assignedIds]);

  const locations = useMemo(() => {
    if (assignedIdSet.size === 0) return allLocations;
    return allLocations.filter((loc) => assignedIdSet.has(loc.id));
  }, [allLocations, assignedIdSet]);

  return {
    /** Filtered locations the current user can access */
    locations,
    /** All locations in the channel (unfiltered) */
    allLocations,
    /** Raw assigned location IDs (empty = unrestricted) */
    assignedLocationIds: assignedIds,
    /** True while either query is loading */
    loading: loadingLocations || loadingAssignments,
    /** Total items from the stock locations query */
    totalItems: locationsData?.stockLocations?.totalItems || 0,
    refetch: refetchLocations,
  };
}
